/**
 * OAuth2 authorize URL for sso-back : **`NEXT_PUBLIC_SSO_OAUTH_AUTHORIZE_BASE`** obligatoire (aucune dérivation depuis l’origine du portail ni chemin relatif **`/sso-api/oauth/authorize`**).
 * Ajustements navigateur (HTTPS document vs `http://` même hostname, rewrite loopback) conservés uniquement sur cette URL explicite.
 */

import { ssoAuthorizeBaseUrl, ssoRedirectUri } from "@Front/lib/sso-auth/config";
import { replaceLoopbackAbsoluteOriginWithDocumentWhenHttps } from "le-coffre-resources/dist/publicBrowserLoopbackSameOriginRewrite";

function trimTrailingSlashes(s: string): string {
	return s.replace(/\/+$/, "");
}

/** `NEXT_PUBLIC_FRONT_APP_HOST` est souvent un **hôte** seul (`test.enso.4nkweb.com`) dans les env-full multisite ; OAuth2 exige un `redirect_uri` absolu avec schéma. */
function ensurePublicOriginHasScheme(originOrHost: string): string {
	const t = originOrHost.trim();
	if (!t) {
		return t;
	}
	if (/^[a-z][a-z0-9+.-]*:/i.test(t)) {
		return t;
	}
	return `https://${t}`;
}

/** Sur page **HTTPS**, une origine **`http://`** sur le **même hostname** que le document devient l’origine courante en **HTTPS**. */
function httpsOriginWhenDocumentIsHttps(originOrUrl: string): string {
	const t = originOrUrl.trim();
	if (typeof window === "undefined" || window.location.protocol !== "https:" || !t.startsWith("http://")) {
		return t;
	}
	try {
		const u = new URL(t.includes("://") ? t : `https://${t}`);
		if (u.hostname.toLowerCase() === window.location.hostname.toLowerCase()) {
			return trimTrailingSlashes(window.location.origin);
		}
	} catch {
		return t;
	}
	return t;
}

function lecoffrePublicOrigin(): string {
	const configured = (process.env["NEXT_PUBLIC_FRONT_APP_HOST"] || "").trim();
	if (typeof window !== "undefined") {
		const withScheme = configured ? ensurePublicOriginHasScheme(configured) : "";
		const raw = trimTrailingSlashes(withScheme || window.location.origin);
		const loopbackFixed = replaceLoopbackAbsoluteOriginWithDocumentWhenHttps(raw);
		return trimTrailingSlashes(httpsOriginWhenDocumentIsHttps(loopbackFixed));
	}
	return trimTrailingSlashes(configured ? ensurePublicOriginHasScheme(configured) : "");
}

/** Prefer `public` value from `GET /v1/public/site-config` when the build omitted `NEXT_PUBLIC_SSO_OAUTH_CLIENT_ID`. */
export function resolveEffectiveSsoOAuthClientId(publicClientIdFromApi?: string | null): string {
	const fromApi = (publicClientIdFromApi ?? "").trim();
	if (fromApi.length > 0) {
		return fromApi;
	}
	return (process.env["NEXT_PUBLIC_SSO_OAUTH_CLIENT_ID"] || "").trim();
}

export function isSsoOAuthClientIdConfigured(publicClientIdFromApi?: string | null): boolean {
	return resolveEffectiveSsoOAuthClientId(publicClientIdFromApi).length > 0;
}

export function ssoAuthorizeBaseUrlFromEnv(): string {
	if (typeof window !== "undefined") {
		return trimTrailingSlashes(
			httpsOriginWhenDocumentIsHttps(
				replaceLoopbackAbsoluteOriginWithDocumentWhenHttps(ssoAuthorizeBaseUrl()),
			),
		);
	}
	const b = (process.env["NEXT_PUBLIC_SSO_OAUTH_AUTHORIZE_BASE"] || "").trim();
	if (!b) {
		throw new Error(
			"SSO OAuth (data-exchange-frontend): NEXT_PUBLIC_SSO_OAUTH_AUTHORIZE_BASE est obligatoire au build (ex. https://sso.4nkweb.com/sso-api/oauth/authorize). Aucun repli same-origin.",
		);
	}
	const coerced = httpsOriginWhenDocumentIsHttps(b);
	return trimTrailingSlashes(replaceLoopbackAbsoluteOriginWithDocumentWhenHttps(coerced));
}

/** URL absolue enregistrée comme `redirect_uri` côté sso-back (OAuth client). */
export function ssoOAuthPublicCallbackUrl(): string {
	if (typeof window !== "undefined") {
		return ssoRedirectUri();
	}
	return `${lecoffrePublicOrigin()}/auth/sso-callback`;
}
